import { useState } from 'react';
import { agentApi } from '../api';
import type { AgentDocument, AgentMemory, DocumentDetail, HealthAnalysisResponse, HealthAnalysisListItem, MemoryHistoryItem } from '../api/agent';

export interface UseTabDataParams {
  selectedUserId: string | null;
  onError: (msg: string) => void;
}

export function useTabData({ selectedUserId, onError }: UseTabDataParams) {
  // Memory state
  const [memories, setMemories] = useState<AgentMemory[]>([]);
  const [memoriesLoading, setMemoriesLoading] = useState(false);

  // Document state
  const [documents, setDocuments] = useState<AgentDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Document detail state
  const [expandedDocumentId, setExpandedDocumentId] = useState<string | null>(null);
  const [expandedDocumentDetail, setExpandedDocumentDetail] = useState<DocumentDetail | null>(null);
  const [loadingDocumentDetail, setLoadingDocumentDetail] = useState(false);

  // Health analysis state
  const [healthAnalyses, setHealthAnalyses] = useState<HealthAnalysisListItem[]>([]);
  const [healthAnalysisLoading, setHealthAnalysisLoading] = useState(false);
  const [expandedAnalysisId, setExpandedAnalysisId] = useState<string | null>(null);
  const [expandedAnalysisDetail, setExpandedAnalysisDetail] = useState<HealthAnalysisResponse | null>(null);
  const [loadingAnalysisDetail, setLoadingAnalysisDetail] = useState(false);

  const loadUserMemories = async (userId: string) => {
    try {
      setMemoriesLoading(true);
      const response = await agentApi.listMemories(userId);
      setMemories(response.memories);
    } catch (err) {
      console.error('Failed to load memories:', err);
      setMemories([]);
    } finally {
      setMemoriesLoading(false);
    }
  };

  const loadUserDocuments = async (userId: string) => {
    try {
      setDocumentsLoading(true);
      const response = await agentApi.listDocuments(userId, { limit: 50 });
      setDocuments(response.documents);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const loadHealthAnalyses = async (userId: string) => {
    try {
      setHealthAnalysisLoading(true);
      const response = await agentApi.listHealthAnalyses(userId, { limit: 20 });
      setHealthAnalyses(response.analyses || []);
    } catch {
      setHealthAnalyses([]);
    } finally {
      setHealthAnalysisLoading(false);
    }
  };

  const loadAnalysisDetail = async (userId: string, analysisId: string) => {
    if (expandedAnalysisId === analysisId) {
      setExpandedAnalysisId(null);
      setExpandedAnalysisDetail(null);
      return;
    }
    try {
      setLoadingAnalysisDetail(true);
      setExpandedAnalysisId(analysisId);
      const detail = await agentApi.getHealthAnalysis(userId, analysisId);
      setExpandedAnalysisDetail(detail);
    } catch (err) {
      console.error('Failed to load analysis detail:', err);
      setExpandedAnalysisDetail(null);
    } finally {
      setLoadingAnalysisDetail(false);
    }
  };

  // Memory history state
  const [memoryHistory, setMemoryHistory] = useState<MemoryHistoryItem[]>([]);
  const [memoryHistoryLoading, setMemoryHistoryLoading] = useState(false);

  const loadMemoryHistory = async (userId: string) => {
    try {
      setMemoryHistoryLoading(true);
      const response = await agentApi.listMemoryHistory(userId, { limit: 100 });
      setMemoryHistory(response.history);
    } catch (err) {
      console.error('Failed to load memory history:', err);
      setMemoryHistory([]);
    } finally {
      setMemoryHistoryLoading(false);
    }
  };

  const handleUpdateMemory = async (memoryId: string, content: string) => {
    if (!selectedUserId) return;
    try {
      await agentApi.updateMemory(selectedUserId, memoryId, content);
      setMemories((prev) =>
        prev.map((m) => (m.memory_id === memoryId ? { ...m, content } : m)),
      );
    } catch (err) {
      console.error('Failed to update memory:', err);
      onError('메모리 수정에 실패했습니다.');
    }
  };

  const handleDeleteMemory = async (memoryId: string) => {
    if (!selectedUserId || !confirm('이 메모리를 삭제하시겠습니까?')) return;

    try {
      await agentApi.deleteMemory(selectedUserId, memoryId);
      setMemories((prev) => prev.filter((m) => m.memory_id !== memoryId));
    } catch (err) {
      console.error('Failed to delete memory:', err);
      onError('메모리 삭제에 실패했습니다.');
    }
  };

  const handleDeleteHealthAnalysis = async (analysisId: string) => {
    if (!selectedUserId || !confirm('이 건강 분석을 삭제하시겠습니까?')) return;

    try {
      await agentApi.deleteHealthAnalysis(selectedUserId, analysisId);
      setHealthAnalyses((prev) => prev.filter((a) => a.analysis_id !== analysisId));
      if (expandedAnalysisId === analysisId) {
        setExpandedAnalysisId(null);
        setExpandedAnalysisDetail(null);
      }
    } catch (err) {
      console.error('Failed to delete health analysis:', err);
      onError('건강 분석 삭제에 실패했습니다.');
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!selectedUserId || !confirm('이 문서를 삭제하시겠습니까?')) return;

    try {
      await agentApi.deleteDocument(selectedUserId, documentId);
      setDocuments((prev) => prev.filter((d) => String(d.id) !== documentId));
    } catch (err) {
      console.error('Failed to delete document:', err);
      onError('문서 삭제에 실패했습니다.');
    }
  };

  const handleFileUpload = async (files: File[]) => {
    if (!selectedUserId || files.length === 0) return;

    try {
      setUploadingFile(true);
      for (const file of files) {
        await agentApi.uploadDocument(selectedUserId, file);
      }
      await loadUserDocuments(selectedUserId);
    } catch (err) {
      console.error('Failed to upload document:', err);
      onError('문서 업로드에 실패했습니다.');
    } finally {
      setUploadingFile(false);
    }
  };

  const loadDocumentDetail = async (userId: string, documentId: string) => {
    if (expandedDocumentId === documentId) {
      setExpandedDocumentId(null);
      setExpandedDocumentDetail(null);
      return;
    }
    try {
      setLoadingDocumentDetail(true);
      setExpandedDocumentId(documentId);
      const detail = await agentApi.getDocument(userId, documentId);
      setExpandedDocumentDetail(detail);
    } catch (err) {
      console.error('Failed to load document detail:', err);
      setExpandedDocumentDetail(null);
    } finally {
      setLoadingDocumentDetail(false);
    }
  };

  const handleToggleDocument = async (documentId: string) => {
    if (!selectedUserId) return;
    const doc = documents.find((d) => String(d.id) === documentId);
    if (!doc) return;

    try {
      const updated = await agentApi.updateDocument(selectedUserId, documentId, {
        is_active: !doc.is_active,
      });
      setDocuments((prev) =>
        prev.map((d) => (String(d.id) === documentId ? { ...d, is_active: updated.is_active } : d)),
      );
    } catch (err) {
      console.error('Failed to toggle document:', err);
      onError('문서 상태 변경에 실패했습니다.');
    }
  };

  return {
    // Memory
    memories,
    memoriesLoading,
    setMemories,
    loadUserMemories,
    handleDeleteMemory,
    handleUpdateMemory,
    memoryHistory,
    memoryHistoryLoading,
    loadMemoryHistory,
    // Document
    documents,
    documentsLoading,
    uploadingFile,
    setDocuments,
    loadUserDocuments,
    handleDeleteDocument,
    handleFileUpload,
    handleToggleDocument,
    expandedDocumentId,
    expandedDocumentDetail,
    loadingDocumentDetail,
    loadDocumentDetail,
    // Health
    healthAnalyses,
    healthAnalysisLoading,
    expandedAnalysisId,
    expandedAnalysisDetail,
    loadingAnalysisDetail,
    setHealthAnalyses,
    setExpandedAnalysisId,
    setExpandedAnalysisDetail,
    loadHealthAnalyses,
    loadAnalysisDetail,
    handleDeleteHealthAnalysis,
  };
}
